import { useEffect, useRef, useState } from "react";
import { api, streamChat } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AppHeader from "@/components/AppHeader";
import PersonaCard from "@/components/PersonaCard";
import VoiceOrb from "@/components/VoiceOrb";
import MessageBubble from "@/components/shared/MessageBubble";
import ArtifactActions from "@/components/shared/ArtifactActions";
import AudioFirstOverlay, { VoiceHelpAction } from "@/components/shared/AudioFirstOverlay";
import ChatComposer from "@/components/shared/ChatComposer";
import useVoiceSession from "@/hooks/useVoiceSession";
import useStickyScroll from "@/hooks/useStickyScroll";
import useThreadAttach from "@/hooks/useThreadAttach";
import { docsFromMessages, messageFromAttach, promptToUseDoc } from "@/lib/attach";
import { friendlyError } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { ArrowDown, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";

const EDIT_GREETING =
  "Sua marca já está registrada. Me diz o que quer ajustar — tom, bandeira, posicionamento — ou anexa um documento que eu atualizo a Brand Persona.";

export default function MinhaMarca() {
  const { refresh } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [persona, setPersona] = useState({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [queued, setQueued] = useState(false);
  const [savingArtifactId, setSavingArtifactId] = useState(null);
  const [orbState, setOrbState] = useState("idle");
  const [showIntro, setShowIntro] = useState(false);
  const [personaSheet, setPersonaSheet] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const feed = useStickyScroll(messages);
  const initRef = useRef(false);
  const queuedRef = useRef(null);
  const abortRef = useRef(null);

  const refreshPersona = async () => {
    try {
      const [{ data }, revs] = await Promise.all([
        api.get("/persona/me"),
        api.get("/persona/me/revisions").catch(() => ({ data: [] })),
      ]);
      setPersona(data || {});
      setRevisions(revs.data || []);
    } catch {}
  };

  const savePersonaField = async (patch) => {
    const { data } = await api.patch("/persona/me", patch);
    setPersona(data || {});
    const revs = await api.get("/persona/me/revisions").catch(() => ({ data: [] }));
    setRevisions(revs.data || []);
  };

  const handleVoiceEvent = (evt) => {
    if (evt.type === "transcript") {
      setMessages((m) => {
        const next = [...m];
        const last = next[next.length - 1];
        if (last && last.role === evt.role && last.live) {
          next[next.length - 1] = { ...last, content: last.content + evt.text };
        } else {
          next.push({ id: `v-${Date.now()}-${Math.random()}`, role: evt.role, content: evt.text, live: true });
        }
        return next;
      });
    } else if (evt.type === "turn_complete") {
      setMessages((m) => m.map((x) => (x.live ? { ...x, live: false } : x)));
      refreshPersona();
      refresh();
    } else if (evt.type === "tool_result") {
      if (evt.name === "update_persona" && evt.result?.ok) refreshPersona();
    } else if (evt.type === "error") {
      toast.error(evt.message || "A sessão de voz falhou. Tente novamente.");
    }
  };

  const voice = useVoiceSession({
    agent: "brand-persona",
    threadId: thread?.id,
    onEvent: handleVoiceEvent,
  });

  const attach = useThreadAttach({
    threadId: thread?.id,
    onAttached: (data) => {
      setMessages((m) => [...m, messageFromAttach(data)]);
    },
  });

  const startVoice = () => {
    setShowIntro(false);
    voice.dismissRecovery?.();
    voice.start();
  };
  const endVoice = () => { voice.stop(); };
  const continueVoiceByText = () => {
    voice.continueWithText?.();
    setShowIntro(false);
  };

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    (async () => {
      try {
        await refreshPersona();
        const ts = await api.get("/threads", { params: { agent_slug: "brand-persona" } });
        const ordered = [...(ts.data || [])].sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
        let t = ordered[0];
        if (!t) {
          const created = await api.post("/threads", { agent_slug: "brand-persona" });
          t = created.data;
        }
        setThread(t);
        const ms = await api.get(`/threads/${t.id}/messages`);
        if (ms.data.length === 0) {
          setMessages([{ id: "greet", role: "assistant", content: EDIT_GREETING }]);
          setShowIntro(true);
        } else {
          setMessages(ms.data);
        }
      } catch (e) {
        toast.error(friendlyError(e));
      }
    })();
  }, []);

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  const attachDraftToLastBubble = (artifactId) => {
    setMessages((m) => {
      const next = [...m];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].role === "assistant") {
          next[i] = { ...next[i], draftArtifactId: artifactId, artifactDecision: null };
          break;
        }
      }
      return next;
    });
  };

  const saveDraftArtifact = async (messageId, artifactId) => {
    setSavingArtifactId(artifactId);
    try {
      await api.post(`/artifacts/${artifactId}/save`);
      setMessages((m) =>
        m.map((x) => (x.id === messageId ? { ...x, artifactDecision: "saved" } : x))
      );
      toast.success("Salvo na Biblioteca ✦");
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setSavingArtifactId(null);
    }
  };

  const detachDoc = async (doc) => {
    if (!thread?.id || !doc?.id) return;
    try {
      await api.delete(`/threads/${thread.id}/attach/${doc.id}`);
      setMessages((m) => m.filter((x) => x.id !== doc.id));
    } catch (e) {
      toast.error(friendlyError(e));
    }
  };

  const sendMessage = async (contentArg) => {
    const content = (contentArg ?? input).trim();
    if (!content || !thread) return;
    if (sending) {
      queuedRef.current = content;
      setQueued(true);
      if (contentArg == null) setInput("");
      return;
    }
    setInput("");
    setShowIntro(false);
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", content }]);
    setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", content: "" }]);
    setSending(true);
    setOrbState("thinking");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await streamChat(thread.id, content, async (evt) => {
        if (evt.type === "token") {
          setOrbState("speaking");
          setMessages((m) => {
            const next = [...m];
            const lastIdx = next.length - 1;
            if (next[lastIdx]?.role === "assistant" && !next[lastIdx].closed) {
              next[lastIdx] = { ...next[lastIdx], content: next[lastIdx].content + evt.content };
              return next;
            }
            return [...next, { id: `a-${Date.now()}-${next.length}`, role: "assistant", content: evt.content }];
          });
        } else if (evt.type === "message_end") {
          setMessages((m) => {
            const next = [...m];
            const lastIdx = next.length - 1;
            if (next[lastIdx]?.role === "assistant") next[lastIdx] = { ...next[lastIdx], closed: true };
            return next;
          });
        } else if (evt.type === "tool_call") {
          setOrbState("tool");
        } else if (evt.type === "tool_result") {
          if (evt.name === "update_persona" && evt.result?.ok) {
            const { data } = await api.get("/persona/me");
            setPersona(data || {});
          }
        } else if (evt.type === "artifact") {
          if (evt.artifact.status === "draft") attachDraftToLastBubble(evt.artifact.id);
        } else if (evt.type === "error") {
          toast.error(evt.message || "A ValiosIA teve um problema ao responder. Tente de novo.");
          setOrbState("idle");
          setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
        } else if (evt.type === "done") {
          setOrbState("idle");
        }
      }, controller.signal);
      await refresh();
    } catch (e) {
      setMessages((m) => m.filter((x) => !(x.role === "assistant" && !x.content)));
      if (e?.name === "AbortError") {
        setOrbState("idle");
      } else {
        toast.error(friendlyError(e));
        setOrbState("error");
      }
    } finally {
      abortRef.current = null;
      setSending(false);
      const pending = queuedRef.current;
      if (pending) {
        queuedRef.current = null;
        setQueued(false);
        setTimeout(() => sendMessage(pending), 0);
      }
    }
  };

  const introVisible = showIntro && !voice.recovery && thread && messages.length <= 1 && !voice.active;
  const orbActive = voice.active ? voice.status : orbState;
  const filled = ["bandeira", "proposito", "personalidade", "tom_de_voz"].filter((f) => persona[f]).length;

  return (
    <div className="h-dvh bg-brand-bg flex flex-col overflow-hidden" data-testid="marca-page">
      <AppHeader />
      <div className="flex-1 min-h-0 w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 pt-3 lg:pt-5 pb-3 grid grid-rows-1 lg:grid-cols-[1.3fr_0.9fr] gap-4 lg:gap-8">
        <div className="relative bg-brand-surface border border-brand-line rounded-3xl flex flex-col min-h-0 overflow-hidden">
          <div className="flex items-center gap-3 lg:gap-4 px-4 lg:px-7 py-3.5 lg:py-4 border-b border-brand-line">
            <VoiceOrb
              state={orbActive}
              size={48}
              levels={voice.active ? voice.levels : null}
              showLabel={false}
            />
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg text-brand-ink truncate">
                {persona?.nome || "Minha marca"}
              </div>
              <div className="text-[12px] text-brand-ink2 font-ui truncate">
                {voice.active ? "conversa por voz ao vivo ✦" : "Ajuste tom, bandeira e posicionamento"}
              </div>
            </div>
            <button
              type="button"
              data-testid="persona-summary-toggle"
              onClick={() => setPersonaSheet(true)}
              className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink2 hover:text-brand-rose transition-colors min-h-[44px]"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {filled}
            </button>
          </div>

          <div
            ref={feed.ref}
            onScroll={feed.onScroll}
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 lg:px-7 py-5 lg:py-6 space-y-4 scrollbar-soft"
            data-testid="marca-chat-feed"
          >
            {messages.map((m) => (
              <div key={m.id}>
                <MessageBubble message={m} />
                {m.draftArtifactId && (
                  <ArtifactActions
                    saving={savingArtifactId === m.draftArtifactId}
                    done={m.artifactDecision || null}
                    onSave={() => saveDraftArtifact(m.id, m.draftArtifactId)}
                    onKeepDraft={() =>
                      setMessages((prev) =>
                        prev.map((x) =>
                          x.id === m.id ? { ...x, artifactDecision: "draft" } : x
                        )
                      )
                    }
                  />
                )}
              </div>
            ))}
          </div>

          {!feed.atBottom && (
            <button
              type="button"
              data-testid="scroll-to-latest"
              onClick={() => feed.scrollToBottom()}
              className="absolute left-1/2 -translate-x-1/2 bottom-24 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-elevated border border-brand-line text-[12px] text-brand-ink shadow-lg hover:border-brand-rose transition-colors z-10"
            >
              <ArrowDown className="w-3.5 h-3.5 text-brand-rose" /> novas mensagens
            </button>
          )}

          <ChatComposer
            value={input}
            onChange={setInput}
            onSend={() => sendMessage()}
            onStop={stopStreaming}
            sending={sending}
            queued={queued}
            placeholder="O que quer ajustar na sua marca?"
            onFiles={thread ? attach.attachFiles : undefined}
            attachItems={attach.items}
            threadDocs={docsFromMessages(messages)}
            onUseDoc={(doc) => sendMessage(promptToUseDoc(doc.filename))}
            onDetachDoc={detachDoc}
            attaching={attach.attaching}
            fileInputRef={attach.inputRef}
            onMicToggle={() => (voice.active ? endVoice() : startVoice())}
            voiceActive={voice.active}
            voiceStatus={voice.status}
            disabled={!thread}
            testids={{ input: "marca-input", send: "marca-send", voice: "marca-voice-toggle", attach: "marca-attach" }}
          />
          {voice.active && !voice.recovery && (
            <VoiceHelpAction onClick={voice.reportAudioProblem} />
          )}

          {voice.recovery && (
            <AudioFirstOverlay
              title={voice.recovery.title}
              description={voice.recovery.message}
              onVoice={voice.resume}
              onText={continueVoiceByText}
              voiceLabel={voice.recovery.actionLabel || "Retomar voz"}
              textLabel="Continuar por texto"
              recovery
            />
          )}

          {introVisible && (
            <AudioFirstOverlay
              title="Quer ajustar sua marca?"
              description="Conversa ao vivo, não uma gravação. Fale ou escreva o que mudou — a ValiosIA atualiza a Brand Persona, não cria outra."
              onVoice={startVoice}
              onText={() => setShowIntro(false)}
            />
          )}
        </div>

        <div className="hidden lg:block min-h-0 overflow-y-auto overflow-x-hidden scrollbar-soft pb-2">
          <PersonaCard
            persona={persona}
            variant="full"
            editable
            onSave={savePersonaField}
            revisions={revisions}
          />
        </div>
      </div>

      <Sheet open={personaSheet} onOpenChange={setPersonaSheet}>
        <SheetContent side="bottom" data-testid="persona-sheet" className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-soft">
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink">Sua marca</SheetTitle>
          </SheetHeader>
          <div className="mt-4">
            <PersonaCard
              persona={persona}
              variant="full"
              editable
              onSave={savePersonaField}
              revisions={revisions}
            />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
